import axios from "axios";
import { BehaviorSubject } from "rxjs";

const env = window.env.REACT_APP_BASE_URL;
const axiosInstance = axios.create({
  baseURL: `${env}`,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: any[] = [];
export const sessionExpired$ = new BehaviorSubject(false);

let userActive = true;
let sessionTimer: NodeJS.Timeout | null = null;

const logoutUser = () => {
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  sessionStorage.clear();
  sessionExpired$.next(true);
};

const resetSessionTimer = () => {
  userActive = true;
  if (sessionTimer) clearTimeout(sessionTimer);

  sessionTimer = setTimeout(() => {
    userActive = false;
    sessionStorage.getItem("redirectedFromUMP") !== "true" && logoutUser();
  }, 10 * 60 * 1000);
};


const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("access_token");
    const operatorID = parseInt(sessionStorage.getItem("tenant_id"));
    const userID = parseInt(sessionStorage.getItem("user_id"));

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (operatorID) {
      config.headers.OperatorId = `${operatorID}`;
    }
    if (userID) {
      config.headers.UserId = `${userID}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401) {
      logoutUser(); 
      throw error;
    }

    if (error.response?.status === 403 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => { throw err });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = sessionStorage.getItem("refresh_token");

      if (!refreshToken) {
        logoutUser();
        throw error;
      }

      try {
        const response = await axios.post(
          `${axiosInstance.defaults.baseURL}/api/v1/refresh`,
          {
            headers: {
              Accept: "application/json",
            },
          },
          { params: { refresh_token: refreshToken } }
        );

        const { access_token, refresh_token } = response.data;

        sessionStorage.setItem("access_token", access_token);
        sessionStorage.setItem("refresh_token", refresh_token);

        axiosInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${access_token}`;
        originalRequest.headers["Authorization"] = `Bearer ${access_token}`;

        processQueue(null, access_token);

        return axiosInstance(originalRequest);
      } catch (err) {
        logoutUser();
        throw err;
      } finally {
        isRefreshing = false;
      }
    }

    throw error;
  }
);

export default axiosInstance;
export { resetSessionTimer, logoutUser };
